'use client';

/**
 * API namespace `coupons` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L7062-L7343).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import { MARKET_CATEGORIES } from '../core/shared-types';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const coupons = {
  /**
   * GET /api/seller/coupons?page=&limit=&status=&includeInactive= —
   * list the seller's own coupons (paginated, 1-indexed). Returns
   * `CouponListResponse` (`coupons` + total + page + limit) — each
   * coupon is the EXPANDED snapshot (`CouponResponse` with the seller's
   * storeName + storeSlug snapshot — same wire shape as the public
   * catalog response per SPEC-242 §3.5).
   *
   * The `status` filter accepts any `CouponStatus` value (or undefined
   * for "all statuses"). The `includeInactive` filter defaults to `true`
   * (the seller dashboard sees soft-deleted coupons for audit) — pass
   * `false` to exclude them.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/coupons), §3.6.1 (catálogo
   *           público UI).
   */
  async list(
    page = 1,
    limit = 20,
    options?: { status?: CouponStatus; includeInactive?: boolean },
    signal?: AbortSignal,
  ): Promise<CouponListResponse> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (options?.status) params.set('status', options.status);
    if (options?.includeInactive !== undefined) {
      params.set('includeInactive', String(options.includeInactive));
    }
    return apiFetch<CouponListResponse>(
      `/seller/coupons?${params.toString()}`,
      { signal },
    );
  },
  /**
   * GET /api/seller/coupons/:couponId — seller's own view of a single
   * coupon. Returns `Coupon` (the entity — NOT the public wire shape).
   *
   * Authz: self only — backend verifies the coupon belongs to the
   * authenticated seller (`sellerId === resolveSellerId(req.user!.sub)`).
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/coupons/:couponId).
   */
  async get(
    couponId: string,
    signal?: AbortSignal,
  ): Promise<Coupon> {
    return apiFetch<Coupon>(
      `/seller/coupons/${encodeURIComponent(couponId)}`,
      { signal },
    );
  },
  /**
   * POST /api/seller/coupons — create a new coupon.
   *
   * Server defaults: `couponId=UUID`, `code=''`, `status='pending
   * _approval'` (INV-MKT-20 — admin must approve before the coupon
   * appears in the public catalog), `active=true`, `redemptionsCount=0`.
   *
   * The Zod `couponCreateSchema` cross-field rules
   * (redemptionUrl required when redemptionMethod='website',
   * redemptionContact required when redemptionMethod in {contact,
   * whatsapp, phone, email}, expiresAt > startsAt) are enforced
   * server-side. The category/subcategory must exist in
   * MARKET_CATEGORIES + the discountValue 1-100 cap for type='percentage'
   * are enforced in the service layer (require DB lookup).
   *
   * Authz: `requireAuth` + `requireActiveSeller`.
   *
   * Spec ref: SPEC-242 §3.3.1 (Coupon model), §3.4.1 (reglas de
   *           negocio), §3.5 (POST /seller/coupons endpoint).
   */
  async create(
    data: CouponCreateInput,
    signal?: AbortSignal,
  ): Promise<Coupon> {
    return apiFetch<Coupon>('/seller/coupons', {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * PUT /api/seller/coupons/:couponId — partial update.
   *
   * Editing a coupon is only allowed when `status='pending_approval'`
   * OR `status='paused'` (mirrors the SPEC-242 §4.3 "Editar bundle"
   * rule from Track D — same UX rule for coupons per SPEC-242 §3.5
   * PUT endpoint comment "Editar cupón (solo si status='pending
   * _approval' o 'paused')"). Enforced in the service layer.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only. Zod
   * `couponUpdateSchema` validates the partial body.
   *
   * Spec ref: SPEC-242 §3.5 (PUT /seller/coupons/:couponId).
   */
  async update(
    couponId: string,
    data: CouponUpdateInput,
    signal?: AbortSignal,
  ): Promise<Coupon> {
    return apiFetch<Coupon>(
      `/seller/coupons/${encodeURIComponent(couponId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        signal,
      },
    );
  },
  /**
   * DELETE /api/seller/coupons/:couponId — soft delete.
   *
   * Sets `active=false` (SPEC-242 §3.3.1 — NOT a 'deleted' status
   * value, same convention as ProductBundle). The coupon is hidden
   * from the public catalog but conserved for the audit trail. The
   * seller can still see it in their dashboard (with an "Inactivo"
   * indicator); admin can see it in the moderation panel.
   *
   * Idempotent: if the coupon is already `active=false`, the service
   * returns success without re-writing.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only +
   * `requireRecent2FA` (TOTP 2FA verified within last 5 min — INV-SEC-06
   * §9.6 — soft-delete is irreversible + creates AuditLog
   * 'COUPON_DELETED'). The frontend opens `TwoFactorFinancialDialog` on
   * 403 `2FA_REQUIRED` (same pattern as the bundle delete flow).
   *
   * Spec ref: SPEC-242 §3.5 (DELETE /seller/coupons/:couponId), §3.3.1
   *           (`active` flag — soft delete);
   *           SPEC-NEGOCIO §9.6 (INV-SEC-06 — 2FA for sensitive ops).
   */
  async delete(
    couponId: string,
    signal?: AbortSignal,
  ): Promise<{ couponId: string; active: false }> {
    return apiFetch<{ couponId: string; active: false }>(
      `/seller/coupons/${encodeURIComponent(couponId)}`,
      { method: 'DELETE', signal },
    );
  },
  /**
   * GET /api/seller/coupons/:couponId/stats — minimal summary view of
   * a coupon's pricing breakdown + redemption counters + status +
   * timestamps.
   *
   * Returns `CouponStats` (locally defined above — mirrors the backend
   * service's interface verbatim). NO sales metrics in this task —
   * future tasks (Track C #17+) will extend this shape with
   * `totalRevenue`, `uniqueBuyersCount`, etc. once the analytics
   * pipeline is implemented.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-242 §3.5 (GET /seller/coupons/:couponId/stats).
   */
  async stats(
    couponId: string,
    signal?: AbortSignal,
  ): Promise<CouponStats> {
    return apiFetch<CouponStats>(
      `/seller/coupons/${encodeURIComponent(couponId)}/stats`,
      { signal },
    );
  },
  /**
   * POST /api/seller/coupons/validate — validate a redemption code
   * (SPEC-242 §3.4.3 — validación de redención). The seller enters the
   * `redemptionCode` (format `CUP-XXXXXXXX`); the backend:
   *   1. Searches `CouponPurchase` by `redemptionCode`.
   *   2. Verifies `status === 'purchased'`, `expiresAt > now`, + that
   *      the purchase's `couponId.sellerId` matches the current seller.
   *   3. On success: marks `status = 'redeemed'`, sets `redeemedAt =
   *      now`, `redeemedBySellerId = current_seller`.
   *   4. Returns the `CouponPurchaseResponse` (with snapshot fields
   *      the seller panel renders: couponTitle, couponBrandName,
   *      pricePaidCredits, redemptionInstructions, etc.).
   *
   * On failure: returns a specific error code (not found / already
   * redeemed / expired / doesn't belong to this seller) — the frontend
   * surfaces the error message via toast.
   *
   * Authz: `requireAuth` + `requireActiveSeller`. NO 2FA required
   * (validation is a state transition on the purchase record, NOT a
   * credit-debit operation — INV-SEC-06 §9.6 only applies to
   * financial-grade state transitions; the credits were already
   * debited at purchase time on the buyer side).
   *
   * Spec ref: SPEC-242 §3.4.3 (validación de redención), §3.5
   *           (POST /seller/coupons/validate endpoint), §3.6.3
   *           (seller UI — validar código de redención).
   */
  async validate(
    redemptionCode: string,
    signal?: AbortSignal,
  ): Promise<CouponPurchaseResponse> {
    return apiFetch<CouponPurchaseResponse>('/seller/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ redemptionCode }),
      signal,
    });
  },
  /**
   * Convenience constant — the 5 valid `CouponStatus` values for the
   * seller dashboard status filter dropdown (avoids hard-coding the
   * union in the page component).
   *
   * 5 values (NOT 6) — `CouponStatus` does NOT include 'deleted'
   * (coupons use the `active: boolean` flag for soft delete per
   * SPEC-242 §3.3.1, NOT a status value — same convention as
   * ProductBundle) NOR 'out_of_stock' (coupons have no physical
   * stock). Adds 'expired' (auto-set by the service layer when
   * `expiresAt` passes per SPEC-242 §3.3.1).
   *
   * §6 Anti-Invención: the values mirror `CouponStatus` verbatim
   * (Track C #9 — `types/coupon.ts`) — NOT invented here.
   *
   * Spec ref: SPEC-242 §3.3.1 (CouponStatus enum), §3.6.3 (seller UI
   *           list table — status filter dropdown).
   */
  STATUS_VALUES: [
    'pending_approval',
    'approved',
    'rejected',
    'paused',
    'expired',
  ] as ReadonlyArray<CouponStatus>,
};
